// src/lib/pharmacyFirst/pathways/aht/aht-wt-liraglutide-rescreen.pathway.ts
//
// AHT Weight Management — Liraglutide (Saxenda®/Biolide®) Full Re-screen Pathway
// Source: AHT_Liraglutide_Clinical_Pathway.docx v1.0
//
// Triggered when a continuation consultation identifies a change since the last
// consultation. The exclusion items the initial pathway confirms via its
// confirmation card are asked here one at a time as individual yes/no questions
// (positive phrasing — a "yes" means the risk is present). The downstream PGD
// checks, interaction checks, cautions, attestation, and outcomes are the
// shared single source of truth.
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.

import type { Pathway } from "../../types/pathway.types";
import {
  PGD_FRESH_CHECKS,
  Q7_CAUTIONS,
  Q8_ATTEST,
  OUT_NO_F2F,
  OUT_CANNOT_SUPPLY,
  OUT_SUPPLY,
  liraglutideScreeningDownstreamNodes,
  liraglutideScreeningOutcomes,
} from "./_shared/liraglutide-screening-downstream";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

const Q1_F2F        = "lira-rescr-q1-f2f";
const EX1_THYROID   = "lira-rescr-ex1-thyroid-men2";
const EX2_PANCR     = "lira-rescr-ex2-pancreatitis";
const EX3_DIABETES  = "lira-rescr-ex3-diabetes";
const EX4_RENAL_HEP = "lira-rescr-ex4-renal-hepatic";
const EX5_GI        = "lira-rescr-ex5-gi-gastroparesis";
const EX6_PREG      = "lira-rescr-ex6-pregnancy";
const EX7_RETINO    = "lira-rescr-ex7-retinopathy";
const DOSE          = "lira-rescr-dose-select";
const ATTEST        = "lira-rescr-dose-confirm";

// ─────────────────────────────────────────────────────────────────────────────
// Pathway
// ─────────────────────────────────────────────────────────────────────────────

export const ahtWtLiraglutideRescreenPathway: Pathway = {
  id: "aht-wt-liraglutide-rescreen",
  name: "Liraglutide (Saxenda®/Biolide®) — Full Re-screen",
  population: "Adults and adolescents attending face-to-face",
  pgdRefs: ["pgd-aht-wt-liraglutide"],
  demographicConfiguration: { bannerFields: [] },
  entryNode: Q1_F2F,

  nodes: [
    // ─────────────────────────────────────────────────────────────────────────
    // Step 1 — Face-to-face confirmation (same as initial)
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q1_F2F,
      type: "yes_no",
      text: "Is the patient attending face-to-face at AHT registered premises?",
      hint: "Face-to-face required for initial supply to adults and ALL supply to adolescents. Subsequent adult supply may be remote (pharmacist physically present at registered premises, GPhC February 2025).",
      answerPolarity: "yes-positive",
      yes: EX1_THYROID,
      no: { outcomeId: OUT_NO_F2F },
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 2 — Exclusion questions (one per confirmation-card item, in order).
    // Positive phrasing: "yes" = risk present → cannot supply.
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: EX1_THYROID,
      type: "yes_no",
      text: "Personal or family history of thyroid cancer or MEN2?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX2_PANCR,
    },
    {
      id: EX2_PANCR,
      type: "yes_no",
      text: "History of pancreatitis (acute or chronic)?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX3_DIABETES,
    },
    {
      id: EX3_DIABETES,
      type: "yes_no",
      text: "Type 1 or Type 3c diabetes mellitus?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX4_RENAL_HEP,
    },
    {
      id: EX4_RENAL_HEP,
      type: "yes_no",
      text: "Severe renal impairment (CrCl <15 mL/min) or severe hepatic impairment?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX5_GI,
    },
    {
      id: EX5_GI,
      type: "yes_no",
      text: "Severe or unstable gastrointestinal disease including gastroparesis?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX6_PREG,
    },
    {
      id: EX6_PREG,
      type: "yes_no",
      text: "Pregnant, breastfeeding, or planning pregnancy?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: EX7_RETINO,
    },
    {
      id: EX7_RETINO,
      type: "yes_no",
      text: "Diabetic retinopathy or macular oedema?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: PGD_FRESH_CHECKS,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Steps 2b–4 — shared screening downstream (PGD fresh checks, interaction
    // checks, cautions review). The shared fixed-dose attestation is dropped and
    // the cautions node is re-routed to the dose selector below, because an
    // established patient is re-escalated to their dose on full re-screen.
    // ─────────────────────────────────────────────────────────────────────────
    ...liraglutideScreeningDownstreamNodes
      .filter((node) => node.id !== Q8_ATTEST)
      .map((node) => (node.id === Q7_CAUTIONS ? { ...node, next: DOSE } : node)),

    // ─────────────────────────────────────────────────────────────────────────
    // Step 5 — Dose selection (pharmacist sets the re-escalated dose).
    // Options mirrored EXACTLY from lira-trans-dose-select. KEEP IN SYNC with
    // aht-wt-liraglutide-transfer.pathway.ts and the drug's source doses.
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: DOSE,
      type: "single_select",
      text: "What dose should be supplied?",
      options: [
        { id: "lira-0_6mg", label: "0.6 mg SC once daily" },
        { id: "lira-1_2mg", label: "1.2 mg SC once daily" },
        { id: "lira-1_8mg", label: "1.8 mg SC once daily" },
        { id: "lira-2_4mg", label: "2.4 mg SC once daily" },
        { id: "lira-3_0mg", label: "3.0 mg SC once daily" },
      ],
      routing: {
        "lira-0_6mg": ATTEST,
        "lira-1_2mg": ATTEST,
        "lira-1_8mg": ATTEST,
        "lira-2_4mg": ATTEST,
        "lira-3_0mg": ATTEST,
      },
      storesTo: "selectedDoseStep",
      required: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 6 — Supply attestation (selected dose). Dose-agnostic wording; the
    // selected dose is captured in selectedDoseStep, mirroring the transfer
    // pathway (no {{selected_dose}} interpolation is wired in the engine).
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: ATTEST,
      type: "attestation",
      text: "Step 5 — Confirm supply of Saxenda®/Biolide® (liraglutide) at the selected daily dose",
      attestationStatement:
        "I confirm that I have:\n" +
        "• Supplied 1 × Saxenda®/Biolide® 6mg/mL 3mL pre-filled pen at the selected daily dose\n" +
        "• Recorded BRAND NAME (Saxenda® or Biolide®) AND batch number (biological product — required at every supply)\n" +
        "• Checked cold chain integrity; pen must not have been frozen\n" +
        "• Checked solution is clear and colourless\n" +
        "• Supplied compatible needles separately (not included in pen)\n" +
        "• Instructed on injection technique and site rotation\n" +
        "• For adolescents: parent/guardian also understands technique and schedule\n" +
        "• Advised patient to inform GP on initiation\n" +
        "• Pancreatitis warning: severe abdominal pain → stop, attend A&E\n" +
        "• Provided patient information leaflet",
      conclusions: {
        optionA: { label: "Confirm and supply", next: { outcomeId: OUT_SUPPLY } },
        optionB: { label: "Do not supply",      next: { outcomeId: OUT_CANNOT_SUPPLY } },
      },
    },
  ],

  outcomes: {
    ...liraglutideScreeningOutcomes,
    [OUT_SUPPLY]: {
      ...liraglutideScreeningOutcomes[OUT_SUPPLY]!,
      title: "Supply complete — Saxenda®/Biolide® (liraglutide), selected dose",
    },
  },
};
